import {
  type Listing,
  ListingType,
  type ListingVariant,
} from '@ps/models/lib/listing';

/**
 * Pure display helpers for the public product detail page. Ported from the
 * `feat/product-surfaces` PDP organism (`libs/ui/src/organisms/product-detail-page/utils`),
 * trimmed to the read-only surface. Purchase/coupon/open-box maths remain
 * out of scope; variant-aware price resolution is supported because the
 * digital/meeting rows + sticky header read a variant-resolved unit price.
 */

/** External + affiliate listings check out on a third-party URL. */
export function isExternalListing(listing: Pick<Listing, 'type'>): boolean {
  return (
    listing.type === ListingType.External ||
    listing.type === ListingType.Affiliate
  );
}

/** Physical (or legacy untyped) listings. */
export function isPhysicalListing(listing: Pick<Listing, 'type'>): boolean {
  return listing.type == null || listing.type === ListingType.Physical;
}

/** Digital + AI-generated-PDF listings. */
export function isDigitalListing(listing: Pick<Listing, 'type'>): boolean {
  return (
    listing.type === ListingType.Digital ||
    listing.type === ListingType.AiGeneratedPdf
  );
}

/** CTA label once the viewer already owns a digital listing. */
export const PURCHASED_DIGITAL_CTA_LABEL = 'Download';

/** Meeting (bookable slot) listings. */
export function isMeetingListing(listing: Pick<Listing, 'type'>): boolean {
  return listing.type === ListingType.Meeting;
}

/**
 * The raw HTML the PDP shows as the listing body. Mirrors RN: a digital
 * landing page with `isLandingEnabled === false` shows the short `summary`
 * instead of the full `description`. Used by both the rendered description
 * row and the SEO meta description so they never diverge.
 */
export function getListingBodyHtml(
  listing: Pick<
    Listing,
    'type' | 'isLandingEnabled' | 'summary' | 'description'
  >,
): string | undefined {
  return listing.type === ListingType.Digital &&
    listing.isLandingEnabled === false
    ? listing.summary
    : listing.description;
}

/**
 * Mirrors RN `isDropshipListing` — a listing is dropship when the seller
 * has linked an upstream supplier. Used to gate the OpenBox banner.
 */
export function isDropshipListing(
  listing: Pick<Listing, 'dropshipDetails'> | null | undefined,
): boolean {
  return listing?.dropshipDetails?.supplierId != null;
}

export type ListingPriceInfo = {
  /** Full price before any discount. */
  price: number;
  /** Discounted price when active, else undefined. */
  discountPrice?: number;
  /** `discountPrice` (when active) or `price`. */
  effectivePrice: number;
  isDiscountActive: boolean;
  isFree: boolean;
};

function isDiscountInWindow(
  listing: Pick<Listing, 'discountStartDate' | 'discountEndDate'>,
): boolean {
  const now = Date.now();
  const start = listing.discountStartDate
    ? new Date(listing.discountStartDate as unknown as string | Date).getTime()
    : -Infinity;
  const end = listing.discountEndDate
    ? new Date(listing.discountEndDate as unknown as string | Date).getTime()
    : Infinity;
  return start <= now && now <= end;
}

/**
 * Resolves the effective price for a listing and (optional) variant.
 * Mirrors the RN PDP's `useSynthesizedListingPriceInfo`: the variant price
 * wins when supplied, then the listing's own price; the discount price
 * overrides whichever base applies when present, enabled, cheaper, and
 * within the active window. A variant discount takes precedence over the
 * listing discount.
 */
export function getListingPriceInfo(
  listing: Pick<
    Listing,
    | 'price'
    | 'discountPrice'
    | 'discountEnabled'
    | 'discountStartDate'
    | 'discountEndDate'
  >,
  variant?: Pick<ListingVariant, 'price' | 'discountPrice'> | null,
): ListingPriceInfo {
  const price = variant?.price ?? listing.price;
  const candidate = variant?.discountPrice ?? listing.discountPrice;
  const isDiscountActive =
    candidate != null &&
    candidate < price &&
    listing.discountEnabled !== false &&
    isDiscountInWindow(listing);

  const discountPrice = isDiscountActive ? candidate : undefined;
  const effectivePrice = discountPrice ?? price;
  return {
    price,
    discountPrice,
    effectivePrice,
    isDiscountActive,
    isFree: effectivePrice <= 0,
  };
}

/** Ordered image URLs for the carousel (default first). */
export function getListingImages(
  listing: Pick<Listing, 'defaultImage' | 'extraImages'>,
): string[] {
  return [listing.defaultImage, ...(listing.extraImages ?? [])].filter(
    (url): url is string => Boolean(url),
  );
}

export type CtaConfig = {
  /** Button label shown on the primary CTA. */
  label: string;
  /** `external` opens `externalCheckoutUrl`; `primary` is the (stubbed) buy flow. */
  variant: 'primary' | 'external';
};

/**
 * Maps a listing type → CTA copy. Mirrors the branch's `getCtaCopy`, minus
 * the quantity/Apple-Pay flags that belong to the deferred purchase flow.
 */
export function getCtaConfig(
  listing: Pick<Listing, 'type' | 'externalProductCtaText'>,
  isFree: boolean,
): CtaConfig {
  switch (listing.type) {
    case ListingType.External:
    case ListingType.Affiliate:
      return {
        label: listing.externalProductCtaText?.trim() || 'Buy Now ↗',
        variant: 'external',
      };
    case ListingType.Digital:
    case ListingType.AiGeneratedPdf:
      return { label: isFree ? 'Get it Now' : 'Buy Now', variant: 'primary' };
    case ListingType.Meeting:
      return { label: 'Book Now', variant: 'primary' };
    default:
      return { label: 'Buy Now', variant: 'primary' };
  }
}

/**
 * Final primary-CTA button title. Mirrors RN's `primaryButtonTitle`
 * (`src/flows/pdp/content.tsx`): paid digital shows "Download Now for $X",
 * external shows "Buy Now ↗" (or the seller's custom CTA when free), and
 * everything else falls back to "Buy Now".
 *
 * The live-show "Auction" / "Show is Over" branches are omitted — they need
 * live-show context the standalone PDP doesn't have (deferred).
 *
 * `priceText` is the already-formatted price (e.g. `"$33.00"`), passed in so
 * this stays a pure string helper with no currency dependency.
 */
export function getPrimaryButtonTitle(
  listing: Pick<Listing, 'type' | 'externalProductCtaText'>,
  isFree: boolean,
  priceText: string,
): string {
  switch (listing.type) {
    case ListingType.Digital:
    case ListingType.AiGeneratedPdf:
      return isFree
        ? listing.externalProductCtaText?.trim() || 'Get it Now'
        : `Download Now for ${priceText}`;
    case ListingType.External:
    case ListingType.Affiliate:
      return isFree
        ? listing.externalProductCtaText?.trim() || 'Get it Now'
        : 'Buy Now ↗';
    case ListingType.Meeting:
      return 'Book Now';
    default:
      return 'Buy Now';
  }
}

/** Weighted-average 0–5 score + total count from a rating histogram. */
export function getRatingScore(rating: Listing['rating']): {
  average: number;
  count: number;
} {
  const details = rating?.details;
  if (details == null) return { average: 0, count: 0 };

  let totalScore = 0;
  let totalCount = 0;
  for (const [key, value] of Object.entries(details)) {
    const score = Number(key);
    if (Number.isNaN(score) || score <= 0 || score > 5 || value == null) {
      continue;
    }
    totalScore += score * value;
    totalCount += value;
  }
  return {
    average: totalCount > 0 ? totalScore / totalCount : 0,
    count: totalCount,
  };
}

/**
 * Max purchasable quantity for a listing/variant. Mirrors RN
 * `maxAvailableQuantity`: fixed-quantity types (digital, meeting, etc.) are
 * always 1; physical uses the selected variant's stock (else the listing's),
 * capped at `maximumPurchaseQuantity`.
 */
export function maxAvailableQuantity(
  listing: Pick<Listing, 'type' | 'quantity' | 'maximumPurchaseQuantity'>,
  variant?: Pick<ListingVariant, 'quantity'> | null,
): number {
  // Listing types with no on-platform inventory — checkout happens off-site
  // (external/affiliate) or the item is inherently single (digital, meeting,
  // etc.). These are fixed-to-one: no quantity stepper, never "sold out".
  // RN's PDP simply omits the quantity selector for all of these.
  if (
    listing.type === ListingType.Digital ||
    listing.type === ListingType.AiGeneratedPdf ||
    listing.type === ListingType.Meeting ||
    listing.type === ListingType.Derived ||
    listing.type === ListingType.AvatarMe ||
    listing.type === ListingType.TokenPack ||
    listing.type === ListingType.External ||
    listing.type === ListingType.Affiliate
  ) {
    return 1;
  }

  const inventory = variant?.quantity ?? listing.quantity ?? 0;
  const cap = listing.maximumPurchaseQuantity;
  if (cap != null) return Math.min(inventory, cap);
  return inventory;
}
